import { Col, FlexboxGrid, Grid, Row, Text, Button } from 'rsuite';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import apiAccess from '../api/apiAccess';
import { selectCurrentIDToken } from '../store/slices/auth/authSelectors';
import {
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressRegistrationVerbiage,
  selectConfigExpressRegistrationRenewEnabled,
} from '../store/slices/config/configSelectors';
import { selectGlobalTranslation } from '../store/slices/globals/globalsSelectors';
import pluralize from 'pluralize';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import dayjs from 'dayjs';
import _ from 'lodash';
import PortalHeader from '../components/layout/PortalHeader';
import IconIndicator from '../components/user-feedback/IconIndicator';
import PermitFilters from '../components/allPermits/PermitFilters';
import PermitList from '../components/allPermits/PermitList';
import Notice from '../components/user-feedback/Notice';
import {
  C_ACTIVE_PERMIT_STATUSES,
  C_DEFAULT_TRANSLATIONS,
} from '../common/consts';
import {
  ALL_PERMITS_STATUS_OPTIONS_ENUM,
  PERMIT_SORT_OPTIONS_ENUM,
} from '../common/enums';
import usePlaceNavigate from '../hooks/usePlaceNavigate';
import {
  getAddressWithUnitNumber,
  isNullUndefinedOrEmptyString,
  titleize,
} from '../common/utils';
import { resetRenewState } from '../store/slices/renew/renewSlice';

const getNameOfOwner = ({ name, firstName, lastName }) =>
  name || `${firstName} ${lastName}`;

const parseOwnerDetails = (ownerDetails) => {
  const ownersArray = Array.isArray(ownerDetails)
    ? ownerDetails.map(getNameOfOwner)
    : [getNameOfOwner(ownerDetails)];
  const owners =
    ownersArray.length > 1
      ? `${ownersArray[0]} (+${ownersArray.length - 1})`
      : ownersArray[0];
  const tooltip = ownersArray.length > 1 ? ownersArray.join(', ') : '';

  return { owners, tooltip, allOwnersArray: ownersArray };
};

const AllPermits = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useDispatch();

  const idToken = useSelector(selectCurrentIDToken);
  const registrationVerbiage = useSelector(
    selectConfigExpressRegistrationVerbiage
  );
  const registrationTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const renewEnabled = useSelector(selectConfigExpressRegistrationRenewEnabled);
  const ownerVerbiage = useSelector(selectGlobalTranslation);

  const [loading, setLoading] = useState(true);
  const [licenses, setLicenses] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [sortFilter, setSortFilter] = useState(
    PERMIT_SORT_OPTIONS_ENUM.ADDRESS_ASC
  );
  const [statusFilter, setStatusFilter] = useState(
    ALL_PERMITS_STATUS_OPTIONS_ENUM.ACTIVE
  );

  useEffect(() => {
    const getUsersLicenses = async () => {
      try {
        setErrorMessage(null);

        const licenses = await apiAccess.getAllLicensesForUserAsync(idToken);

        let renewableLicenses = [];
        if (renewEnabled) {
          const renewableLicensesResult =
            await apiAccess.getUsersRenewableLicensesAsync(idToken);
          if (renewableLicensesResult.status === 200) {
            renewableLicenses = renewableLicensesResult.data;
          }
        }

        // convert licenses for display
        let convertedLicenses = [];

        if (!isNil(licenses) && !isEmpty(licenses)) {
          convertedLicenses = licenses.map((license) => {
            const { owners, allOwnersArray, tooltip } = parseOwnerDetails(
              license.owner
            );
            const canRenew =
              renewEnabled &&
              license.license &&
              renewableLicenses.find(
                (renewLicense) => renewLicense.license === license.license
              );
            const fullAddress = getAddressWithUnitNumber(
              license.property_address,
              license.unit_number
            );

            let displayExpirationDate = null;
            if (!isNullUndefinedOrEmptyString(license.expiration_date)) {
              const isExpirationDateValid = dayjs(
                license.expiration_date
              ).isValid();
              displayExpirationDate = isExpirationDateValid
                ? dayjs(license.expiration_date).format('YYYY-MM-DD')
                : titleize(license.expiration_date);
            }

            return {
              address: fullAddress,
              licenseNumber: license.license,
              applicationNumber: license.application_number,
              status: license.status,
              expirationDate: displayExpirationDate,
              ownersNames: owners,
              ownersNameToolTip: tooltip,
              allOwnersArray: allOwnersArray,
              canRenew: canRenew,
            };
          });
        }

        setLicenses(convertedLicenses);
      } catch (err) {
        setErrorMessage(err.message);
        Sentry.captureException(err);
      } finally {
        setLoading(false);
      }
    };

    if (idToken) {
      setLoading(true);
      getUsersLicenses();
    }
  }, [idToken, renewEnabled]);

  useEffect(() => {
    dispatch(resetRenewState());
  }, [dispatch]);

  const ownerLabel = useMemo(
    () => ownerVerbiage ?? C_DEFAULT_TRANSLATIONS.owner,
    [ownerVerbiage]
  );

  const statusOptions = useMemo(
    () => [
      {
        name: 'All Statuses',
        value: ALL_PERMITS_STATUS_OPTIONS_ENUM.ALL,
      },
      {
        name: `Active  ${pluralize(registrationTypeVerbiage)}`,
        value: ALL_PERMITS_STATUS_OPTIONS_ENUM.ACTIVE,
      },
      {
        name: `Inactive ${pluralize(registrationTypeVerbiage)}`,
        value: ALL_PERMITS_STATUS_OPTIONS_ENUM.INACTIVE,
      },
    ],
    [registrationTypeVerbiage]
  );

  const sortOptions = useMemo(
    () => [
      {
        name: 'Address, A -> Z',
        value: PERMIT_SORT_OPTIONS_ENUM.ADDRESS_ASC,
      },
      {
        name: 'Address, Z -> A',
        value: PERMIT_SORT_OPTIONS_ENUM.ADDRESS_DESC,
      },
      {
        name: `${registrationTypeVerbiage} Number, smallest to biggest`,
        value: PERMIT_SORT_OPTIONS_ENUM.PERMIT_NUMBER_ASC,
      },
      {
        name: `${registrationTypeVerbiage} Number, biggest to smallest`,
        value: PERMIT_SORT_OPTIONS_ENUM.PERMIT_NUMBER_DESC,
      },
      {
        name: `${registrationTypeVerbiage} Status, A -> Z`,
        value: PERMIT_SORT_OPTIONS_ENUM.STATUS_ASC,
      },
      {
        name: `${registrationTypeVerbiage} Status, Z -> A`,
        value: PERMIT_SORT_OPTIONS_ENUM.STATUS_DESC,
      },
      {
        name: `Expiration Date, soonest first`,
        value: PERMIT_SORT_OPTIONS_ENUM.EXPIRATION_DATE_DESC,
      },
      {
        name: `Expiration Date, furthest first`,
        value: PERMIT_SORT_OPTIONS_ENUM.EXPIRATION_DATE_ASC,
      },
      {
        name: `${pluralize(ownerLabel)} Name, A -> Z`,
        value: PERMIT_SORT_OPTIONS_ENUM.OWNER_NAME_ASC,
      },
      {
        name: `${pluralize(ownerLabel)} Name, Z -> A`,
        value: PERMIT_SORT_OPTIONS_ENUM.OWNER_NAME_DESC,
      },
    ],
    [ownerLabel, registrationTypeVerbiage]
  );

  const sortConfig = useMemo(
    () => ({
      [PERMIT_SORT_OPTIONS_ENUM.ADDRESS_ASC]: { key: 'address', order: 'asc' },
      [PERMIT_SORT_OPTIONS_ENUM.ADDRESS_DESC]: {
        key: 'address',
        order: 'desc',
      },
      [PERMIT_SORT_OPTIONS_ENUM.PERMIT_NUMBER_ASC]: {
        key: 'permitNumber',
        order: 'asc',
      },
      [PERMIT_SORT_OPTIONS_ENUM.PERMIT_NUMBER_DESC]: {
        key: 'permitNumber',
        order: 'desc',
      },
      [PERMIT_SORT_OPTIONS_ENUM.STATUS_ASC]: { key: 'status', order: 'asc' },
      [PERMIT_SORT_OPTIONS_ENUM.STATUS_DESC]: { key: 'status', order: 'desc' },
      [PERMIT_SORT_OPTIONS_ENUM.EXPIRATION_DATE_ASC]: {
        key: 'expirationDate',
        order: 'asc',
      },
      [PERMIT_SORT_OPTIONS_ENUM.EXPIRATION_DATE_DESC]: {
        key: 'expirationDate',
        order: 'desc',
      },
      [PERMIT_SORT_OPTIONS_ENUM.OWNER_NAME_ASC]: {
        key: 'ownersNames',
        order: 'asc',
      },
      [PERMIT_SORT_OPTIONS_ENUM.OWNER_NAME_DESC]: {
        key: 'ownersNames',
        order: 'desc',
      },
    }),
    [PERMIT_SORT_OPTIONS_ENUM]
  );

  const filteredLicenses = useMemo(() => {
    const matchesSearchFilter = (license) => {
      if (searchFilter === '') return true; // Handle empty search filter

      const lowercasedFilter = searchFilter.toLowerCase();
      return (
        license.address.toLowerCase().includes(lowercasedFilter) ||
        license.licenseNumber?.toLowerCase().includes(lowercasedFilter) ||
        license.allOwnersArray?.some((owner) =>
          owner.toLowerCase().includes(lowercasedFilter)
        )
      );
    };

    const matchesStatusFilter = (license) => {
      if (statusFilter === ALL_PERMITS_STATUS_OPTIONS_ENUM.ALL) {
        return true;
      }
      if (statusFilter === ALL_PERMITS_STATUS_OPTIONS_ENUM.ACTIVE) {
        return C_ACTIVE_PERMIT_STATUSES.includes(license.status);
      }
      if (statusFilter === ALL_PERMITS_STATUS_OPTIONS_ENUM.INACTIVE) {
        return !C_ACTIVE_PERMIT_STATUSES.includes(license.status);
      }
      return false;
    };

    const filtered = licenses?.filter(
      (license) => matchesSearchFilter(license) && matchesStatusFilter(license)
    );

    return filtered;
  }, [licenses, searchFilter, statusFilter]);

  const sortedLicenses = useMemo(() => {
    const { key, order } = sortConfig[sortFilter];

    let sortedArray = _.sortBy(filteredLicenses, (item) => {
      const value = item[key];
      return value === null || value === undefined ? '' : value;
    });

    if (order === 'desc') {
      sortedArray = sortedArray.reverse();
    }

    return sortedArray;
  }, [filteredLicenses, sortFilter, sortConfig, statusFilter]);

  return (
    <main>
      <FlexboxGrid justify="space-between">
        <FlexboxGrid.Item colspan={24}>
          <PortalHeader title={`My ${pluralize(registrationTypeVerbiage)}`} />
        </FlexboxGrid.Item>

        <FlexboxGrid.Item
          className="mt-24 pb-24 ph-48"
          colspan={24}
        >
          <Grid fluid>
            <Row className="mb-24">
              <Col xs={24}>
                <Button
                  appearance="subtle"
                  className="btn-link-underline-hover"
                  onClick={() => {
                    placeNavigate('/dashboard');
                  }}
                >
                  &lt; Back
                </Button>
              </Col>
            </Row>
            <Row className="mb-20">
              <Col xs={24}>
                <Text className="header-text-h3">
                  My {pluralize(registrationVerbiage)}
                </Text>
              </Col>
            </Row>
            {loading && <IconIndicator />}
            {!loading && errorMessage && <Notice message={errorMessage} />}
            {!loading && !errorMessage && (
              <>
                <PermitFilters
                  searchFilter={searchFilter}
                  setSearchFilter={setSearchFilter}
                  sortFilter={sortFilter}
                  setSortFilter={setSortFilter}
                  sortOptions={sortOptions}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  statusOptions={statusOptions}
                  registrationVerbiage={registrationVerbiage}
                  registrationTypeVerbiage={registrationTypeVerbiage}
                  ownerLabel={ownerLabel}
                />
                <PermitList
                  licenses={sortedLicenses}
                  registrationTypeVerbiage={registrationTypeVerbiage}
                  ownerLabel={ownerLabel}
                />
              </>
            )}
          </Grid>
        </FlexboxGrid.Item>
      </FlexboxGrid>
    </main>
  );
};

export default AllPermits;
